import {isClient} from 'hec/utils/common'
import Cookies from 'js-cookie'
import {TpaGroupNames, TpaTrackingCookieValues} from '../types'
import {getTpaParamsFromUrl, getExpiryDateAndTimeForTpaCookies} from '../utils'

/* ----------------------------------------
 * Constants & Basic Helpers
 * ---------------------------------------- */

const EMPTY_ATTRIBUTION_VALUE = '~~'
const TPA_COOKIE_NAME = 'TPA'

const isCookieAttributionEmpty = (attribution: string) => attribution === EMPTY_ATTRIBUTION_VALUE

const extractFirstTimestampFromCookie = (cookieValue: string): number | null => {
    return (
        cookieValue
            .split('|')
            .map((group) => Number(group.split('~')[1]))
            .find((ts) => !isNaN(ts)) ?? null
    )
}

/* ----------------------------------------
 * Attribution Utilities
 * ---------------------------------------- */

export const areAttributionGroupsEqual = (cookieA: string, cookieB: string): boolean => {
    const clean = (cookie: string) =>
        cookie
            .split('|')
            .map((group) => {
                const [part1 = '', , part3 = ''] = group.split('~')
                return `${part1}~${part3}`
            })
            .join('|')

    return clean(cookieA) === clean(cookieB)
}

export const determineShouldUpdateTpaTrackingCookie = (
    cookieA: string,
    cookieB: string
): boolean => {
    const [tpaA, parentA, brokerA] = cookieA.split('|')
    const [tpaB, parentB, brokerB] = cookieB.split('|')

    return !(
        areAttributionGroupsEqual(tpaA, tpaB) &&
        areAttributionGroupsEqual(parentA, parentB) &&
        areAttributionGroupsEqual(brokerA, brokerB)
    )
}

export const getAttributionGroupFromTpaTrackingValue = (value: string): TpaGroupNames => {
    const [tpa, parent, broker] = value.split('|')

    if (!isCookieAttributionEmpty(tpa)) return TpaGroupNames.TPA
    if (!isCookieAttributionEmpty(broker)) return TpaGroupNames.BROKER
    if (!isCookieAttributionEmpty(parent)) return TpaGroupNames.PARENT

    return TpaGroupNames.TPA
}

export const parseRevTrackingFields = (
    value: string
): {afid: string; groupName: TpaGroupNames; cid: string} => {
    const groupName = getAttributionGroupFromTpaTrackingValue(value)
    const groupIndex = {tpa: 0, parent: 1, broker: 2}[groupName]
    const raw = value.split('|')[groupIndex]
    const [afid = '', , cid = ''] = raw.split('~')

    return {afid, groupName, cid}
}

/* ----------------------------------------
 * Cookie Formatters
 * ---------------------------------------- */

export const formatTpaTrackingCookie = ({
    groupName,
    cid,
    afid,
    parentId,
    expiryMs
}: TpaTrackingCookieValues & {expiryMs: number}) => {
    const tpa =
        groupName === TpaGroupNames.TPA ? [afid, expiryMs, cid].join('~') : EMPTY_ATTRIBUTION_VALUE
    const parent =
        parentId || groupName === TpaGroupNames.PARENT
            ? [parentId || afid, expiryMs, cid].join('~')
            : EMPTY_ATTRIBUTION_VALUE
    const broker =
        groupName === TpaGroupNames.BROKER
            ? [afid, expiryMs, cid].join('~')
            : EMPTY_ATTRIBUTION_VALUE

    return [tpa, parent, broker].join('|')
}

/* ----------------------------------------
 * Cookie Setters
 * ---------------------------------------- */

export const setTpaTrackingCookiesFromUrl = () => {
    if (!isClient) return

    // resolveAffiliate: also accept legacy `a_aid` affiliate links (SFCC-11532).
    const trackingValues = getTpaParamsFromUrl({resolveAffiliate: true})
    if (!trackingValues) return

    const {afid, groupName, cid, parentId} = trackingValues
    const {expiryDate, expiryMs} = getExpiryDateAndTimeForTpaCookies(groupName)

    const cookie = formatTpaTrackingCookie({afid, groupName, cid, parentId, expiryMs})
    Cookies.set(TPA_COOKIE_NAME, cookie, {expires: expiryDate})
}

export const setRevTrackingCookiesFromCustomerData = (value: string) => {
    const timestamp = extractFirstTimestampFromCookie(value)
    const group = getAttributionGroupFromTpaTrackingValue(value)
    const {expiryDate: fallbackDate} = getExpiryDateAndTimeForTpaCookies(group)

    Cookies.set(TPA_COOKIE_NAME, value, {
        expires: timestamp ? new Date(timestamp) : fallbackDate
    })
}

/* ----------------------------------------
 * Cookie Getters
 * ---------------------------------------- */

export const getTpaTrackingValuesFromCookies = (): TpaTrackingCookieValues | undefined => {
    const cookie = Cookies.get(TPA_COOKIE_NAME)
    if (!cookie) return

    const [tpa, parent, broker] = cookie.split('|')
    const isTpaValid = !isCookieAttributionEmpty(tpa)
    const isParentValid = !isCookieAttributionEmpty(parent)
    const isBrokerValid = !isCookieAttributionEmpty(broker)

    const values: TpaTrackingCookieValues = {
        afid: '',
        groupName: '',
        cid: '',
        parentId: ''
    }

    if (isParentValid) {
        const [parentId, , cid] = parent.split('~')
        values.parentId = parentId
        values.cid = cid
        values.groupName = isTpaValid ? TpaGroupNames.TPA : TpaGroupNames.PARENT
    }

    if (isTpaValid || isBrokerValid) {
        const source = isBrokerValid ? broker : tpa
        const group = isBrokerValid ? TpaGroupNames.BROKER : TpaGroupNames.TPA
        const [afid, , cid] = source.split('~')
        values.afid = afid
        values.groupName = group
        values.cid = cid
    }

    return values
}
